import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { cityTags, bizCountries, api, categories, ngStates, go, logo } from './util';
const toLines = a => (a || []).join('\n');
const fromLines = s => String(s || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean);

/**
 * /businesses/new and /businesses/{slug}?edit=1: add or change a business. New listings (and changed ones that were
 * sent back) wait for a moderator. Owners can also ask for the registration (CAC in Nigeria) to be checked and
 * delete the business here. The server checks every field again (Businesses::clean).
 */
export default class BusinessForm extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.slug = vnode.attrs.slug || null;
    this.b = null;
    this.saving = false;
    this.uploading = false;
    this.error = null;
    this.reg = {
      country: 'NG',
      number: '',
      name: ''
    };
    this.f = {
      name: '',
      logoUrl: '',
      category: '',
      subcategories: [],
      operatingStatus: 'operating',
      yearEstablished: '',
      about: '',
      country: 'NG',
      stateId: '',
      city: '',
      serviceAreas: '',
      services: '',
      products: '',
      url: '',
      email: '',
      phone: ''
    };
    // a new business has two people from the start: you, and a co-owner (username, or email if not on Voucha)
    this.partner = '';
    if (this.slug) {
      app.request({
        method: 'GET',
        url: api('/businesses/' + this.slug),
        errorHandler: () => {}
      }).then(r => this.fill(r.business)).catch(() => this.error = 'This business could not be found.').then(() => m.redraw());
    }
  }
  fill(b) {
    this.b = b;
    const c = b.contact || {};
    this.f = {
      name: b.name || '',
      logoUrl: b.logoUrl || '',
      category: b.industryKey || b.category && b.category.key || '',
      subcategories: b.subcategoryKeys || [],
      operatingStatus: b.operatingStatus || 'operating',
      yearEstablished: b.yearEstablished ? String(b.yearEstablished) : '',
      about: b.about || '',
      country: b.country || '',
      stateId: b.stateId ? String(b.stateId) : '',
      city: b.city || '',
      serviceAreas: toLines(b.serviceAreas),
      services: toLines(b.services),
      products: toLines(b.products),
      url: c.url || '',
      email: c.email || '',
      phone: c.phone || ''
    };
    const r = b.registration || {};
    this.reg = {
      country: r.country || b.country || 'NG',
      number: r.number || '',
      name: r.name || b.name
    };
  }
  set(key) {
    return e => {
      this.f[key] = e.target.value;
      if (key === 'category') this.f.subcategories = [];
      if (key === 'country') {
        this.f.city = '';
        this.f.stateId = '';
        this.cityOther = false;
      }
      this.error = null;
    };
  }
  toggleSub(k) {
    const s = this.f.subcategories;
    this.f.subcategories = s.includes(k) ? s.filter(x => x !== k) : s.length < 6 ? s.concat(k) : s;
  }
  upload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const body = new FormData();
    body.append('image', file);
    this.uploading = true;
    app.request({
      method: 'POST',
      url: api('/events-image'),
      body,
      serialize: raw => raw,
      errorHandler: () => {}
    }).then(r => {
      if (r && r.errors) this.error = r.errors.image;else this.f.logoUrl = r.url;
    }).catch(() => this.error = 'The logo could not be uploaded.').then(() => {
      this.uploading = false;
      e.target.value = '';
      m.redraw();
    });
  }

  /**
   * Where the country has city tags (Canada, the UK, the US), the city is picked from them, so the business shows
   * on that city's tag page; "Another city" types any other. Elsewhere (Nigeria: the state links it) it's typed.
   */
  cityField(f) {
    const tags = cityTags(f.country);
    if (!tags.length) return m("input", {
      maxlength: "100",
      value: f.city,
      oninput: this.set('city'),
      placeholder: "City (optional)",
      "aria-label": "City"
    });
    const known = tags.some(t => t.name.toLowerCase() === String(f.city || '').toLowerCase());
    const other = this.cityOther || !!f.city && !known;
    return [m("select", {
      className: "VBiz-select",
      value: other ? '__other' : known ? tags.find(t => t.name.toLowerCase() === f.city.toLowerCase()).name : '',
      onchange: e => {
        const v = e.target.value;
        this.cityOther = v === '__other';
        f.city = v === '__other' || v === '' ? '' : v;
      },
      "aria-label": "City"
    }, m("option", {
      value: ""
    }, "City"), tags.map(t => m("option", {
      value: t.name
    }, t.name)), m("option", {
      value: "__other"
    }, "Another city\u2026")), other ? m("input", {
      maxlength: "100",
      value: f.city,
      oninput: this.set('city'),
      placeholder: "Which city?",
      "aria-label": "Other city"
    }) : null];
  }
  body() {
    const f = this.f;
    let url = f.url.trim();
    if (url && !/^https?:\/\//i.test(url)) url = 'https://' + url;
    return {
      name: f.name.trim(),
      logoUrl: f.logoUrl,
      category: f.category,
      subcategories: f.subcategories,
      operatingStatus: f.operatingStatus,
      yearEstablished: f.yearEstablished ? Number(f.yearEstablished) : null,
      about: f.about.trim(),
      country: f.country,
      stateId: f.country === 'NG' && f.stateId ? Number(f.stateId) : null,
      city: f.city.trim(),
      serviceAreas: fromLines(f.serviceAreas),
      services: fromLines(f.services),
      products: fromLines(f.products),
      url,
      email: f.email.trim(),
      phone: f.phone.trim()
    };
  }
  submit(e) {
    e.preventDefault();
    if (!this.f.name.trim()) return this.error = 'Give the business a name.';
    if (!this.f.category) return this.error = 'Choose an industry.';
    if (this.f.country === 'NG' && !this.f.stateId) return this.error = 'Choose the state it’s in, so it shows on that state’s page.';
    if (!this.b && !this.partner.trim()) return this.error = 'Add a co-owner: their Voucha username, or their email if they aren’t on Voucha yet.';
    this.saving = true;
    this.error = null;
    app.request({
      method: 'POST',
      url: api('/businesses' + (this.b ? '/' + this.b.id : '')),
      body: this.b ? this.body() : Object.assign(this.body(), {
        partner: this.partner.trim(),
        partnerRole: 'owner'
      }),
      errorHandler: () => {}
    }).then(r => {
      const b = r.business;
      app.alerts.show({
        type: 'success'
      }, b.approval === 'approved' ? 'Saved.' : 'Thanks. ' + b.name + ' will be listed once a moderator has checked it.');
      m.route.set('/businesses/' + b.slug);
    }).catch(err => {
      this.saving = false;
      this.error = err && err.response && err.response.message || 'That couldn’t be saved. Please try again.';
      m.redraw();
    });
  }
  requestVerification() {
    this.verifying = true;
    this.vmsg = null;
    app.request({
      method: 'POST',
      url: api('/businesses/' + this.b.id + '/verification'),
      body: this.reg,
      errorHandler: () => {}
    }).then(r => this.fill(r.business)).catch(err => this.vmsg = err && err.response && err.response.message || 'That couldn’t be sent.').then(() => {
      this.verifying = false;
      m.redraw();
    });
  }
  remove() {
    if (!confirm('Delete ' + this.b.name + '? Its page, vouches and team go too. Its discussions stay.')) return;
    app.request({
      method: 'DELETE',
      url: api('/businesses/' + this.b.id),
      errorHandler: () => {}
    }).then(() => m.route.set('/businesses')).catch(() => {
      this.error = 'It couldn’t be deleted.';
      m.redraw();
    });
  }
  view() {
    if (!app.session.user) {
      return m("div", {
        className: "VEvt VEvt-empty"
      }, m("p", null, "Log in or sign up to add your business."));
    }
    if (this.slug && !this.b) return this.error ? m("div", {
      className: "VEvt VEvt-empty"
    }, m("p", null, this.error)) : m(LoadingIndicator, null);
    const f = this.f;
    const cat = categories().find(c => c.key === f.category);
    const year = new Date().getFullYear();
    const back = this.b ? '/businesses/' + this.b.slug : '/businesses';
    const block = (icon, title, sub, body) => m("div", {
      className: "VEvtF-block"
    }, m("div", {
      className: "VEvtF-blockHead"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, title), sub ? m("small", null, sub) : null)), body);
    return m("form", {
      className: "VEvt VEvtF VBizF",
      onsubmit: e => this.submit(e)
    }, m("div", {
      className: "VEvtD-bar"
    }, m("a", {
      href: back,
      onclick: go(back)
    }, "\u2190 ", this.b ? this.b.name : 'Businesses')), this.error ? m("p", {
      className: "VEvtF-alert"
    }, this.error) : null, m("div", {
      className: "VEvtF-layout"
    }, m("div", {
      className: "VEvtF-side"
    }, m("label", {
      className: "VEvtF-coverBox"
    }, logo({
      name: f.name || '?',
      logoUrl: f.logoUrl
    }, 0), m("span", {
      className: "VEvtF-coverButton"
    }, m("i", {
      className: this.uploading ? 'fas fa-spinner fa-spin' : 'fas fa-image',
      "aria-hidden": "true"
    })), m("input", {
      type: "file",
      accept: "image/jpeg,image/png,image/webp",
      onchange: e => this.upload(e)
    })), m("p", {
      className: "VEvtF-hint"
    }, f.logoUrl ? 'Tap the logo to change it. ' : 'Add your logo (square works best). Without one, your initials are used.', f.logoUrl ? m("button", {
      type: "button",
      className: "VBiz-linkBtn",
      onclick: () => f.logoUrl = ''
    }, "Remove") : null)), m("div", {
      className: "VEvtF-main"
    }, m("div", {
      className: "VEvtF-title"
    }, m("input", {
      maxlength: "120",
      value: f.name,
      oninput: this.set('name'),
      placeholder: "Business name",
      "aria-label": "Business name"
    })), m("textarea", {
      className: "VBizF-about",
      rows: "3",
      maxlength: "300",
      value: f.about,
      oninput: this.set('about'),
      placeholder: "What it does, in a sentence or two",
      "aria-label": "What it does"
    }), m("p", {
      className: "VEvtF-hint VBizF-count"
    }, 300 - f.about.length, " characters left"), block('fas fa-briefcase', 'Industry', 'Pick one, then up to 6 specialities', [m("select", {
      className: "VBiz-select",
      value: f.category,
      onchange: this.set('category'),
      "aria-label": "Industry"
    }, m("option", {
      value: ""
    }, "Choose an industry"), categories().map(c => m("option", {
      value: c.key
    }, c.name))), cat && cat.sub.length ? m("div", {
      className: "VBiz-pills"
    }, cat.sub.map(s => m("button", {
      type: "button",
      className: 'VBiz-pill' + (f.subcategories.includes(s.key) ? ' is-on' : ''),
      onclick: () => this.toggleSub(s.key)
    }, f.subcategories.includes(s.key) ? m("i", {
      className: "fas fa-check",
      "aria-hidden": "true"
    }) : null, " ", s.name))) : null]), block('fas fa-location-dot', 'Location', 'Where it’s based, and the areas it serves', [m("div", {
      className: "VBizF-row"
    }, m("select", {
      className: "VBiz-select",
      value: f.country,
      onchange: this.set('country'),
      "aria-label": "Country"
    }, m("option", {
      value: ""
    }, "Country"), bizCountries(f.country).map(([code, name]) => m("option", {
      value: code
    }, name))), f.country === 'NG' ? m("select", {
      className: "VBiz-select",
      value: f.stateId,
      onchange: this.set('stateId'),
      "aria-label": "State"
    }, m("option", {
      value: ""
    }, "State"), ngStates().map(s => m("option", {
      value: s.id
    }, s.name))) : null, this.cityField(f)), m("textarea", {
      rows: "2",
      value: f.serviceAreas,
      oninput: this.set('serviceAreas'),
      placeholder: "Areas served, one per line (e.g. Lagos, Nationwide, West Africa)",
      "aria-label": "Areas served"
    })]), block('fas fa-box-open', 'What you offer', 'One per line', [m("textarea", {
      rows: "3",
      value: f.services,
      oninput: this.set('services'),
      placeholder: "Services (e.g. Bookkeeping, Tax filing)",
      "aria-label": "Services"
    }), m("textarea", {
      rows: "2",
      value: f.products,
      oninput: this.set('products'),
      placeholder: "Products (optional)",
      "aria-label": "Products"
    })]), !this.b ? block('fas fa-user-group', 'Co-owner', 'Every business has at least two people, so it’s never left without someone who can look after it', [m("input", {
      value: this.partner,
      oninput: e => (this.partner = e.target.value, this.error = null),
      placeholder: "Their Voucha username, or their email",
      "aria-label": "Co-owner",
      autocomplete: "off"
    }), m("p", {
      className: "VEvtF-hint VBizF-partnerHint"
    }, /@/.test(this.partner) && !/^@/.test(this.partner.trim()) ? 'Not on Voucha yet? We’ll email them a link to join and become a co-owner.' : 'They’ll get an invitation to accept. You can add more people once it’s saved.')]) : null, m("h4", {
      className: "VEvtF-optionsTitle"
    }, "Details"), m("div", {
      className: "VEvtF-options"
    }, m("label", {
      className: "VEvtF-option"
    }, m("i", {
      className: "fas fa-signal",
      "aria-hidden": "true"
    }), m("span", null, "Status"), m("select", {
      value: f.operatingStatus,
      onchange: this.set('operatingStatus'),
      "aria-label": "Status"
    }, m("option", {
      value: "operating"
    }, "Operating"), m("option", {
      value: "paused"
    }, "Paused"), m("option", {
      value: "closed"
    }, "Closed"))), m("label", {
      className: "VEvtF-option"
    }, m("i", {
      className: "far fa-calendar",
      "aria-hidden": "true"
    }), m("span", null, "Year established"), m("input", {
      type: "number",
      min: 1900,
      max: year,
      value: f.yearEstablished,
      oninput: this.set('yearEstablished'),
      placeholder: "Optional",
      "aria-label": "Year established"
    })), m("label", {
      className: "VEvtF-option"
    }, m("i", {
      className: "fas fa-globe",
      "aria-hidden": "true"
    }), m("span", null, "Website"), m("input", {
      type: "text",
      inputmode: "url",
      value: f.url,
      oninput: this.set('url'),
      placeholder: "example.com",
      "aria-label": "Website"
    })), m("label", {
      className: "VEvtF-option"
    }, m("i", {
      className: "far fa-envelope",
      "aria-hidden": "true"
    }), m("span", null, "Email"), m("input", {
      type: "email",
      value: f.email,
      oninput: this.set('email'),
      placeholder: "Optional",
      "aria-label": "Email"
    })), m("label", {
      className: "VEvtF-option"
    }, m("i", {
      className: "fas fa-phone",
      "aria-hidden": "true"
    }), m("span", null, "Phone"), m("input", {
      type: "tel",
      value: f.phone,
      oninput: this.set('phone'),
      placeholder: "Optional",
      "aria-label": "Phone"
    })), !this.b ? m("div", {
      className: "VEvtF-option is-info"
    }, m("i", {
      className: "fas fa-user-check",
      "aria-hidden": "true"
    }), m("span", null, "Checked by a moderator before it\u2019s listed. You\u2019ll be its owner.")) : null), m(Button, {
      className: "Button Button--primary VEvtF-submit",
      type: "submit",
      loading: this.saving,
      disabled: this.uploading
    }, this.b ? 'Save changes' : 'Submit for review'), this.b && this.b.canEdit ? this.verification() : null, this.b && (this.b.canEdit || this.b.canModerate) ? m("div", {
      className: "VEvt-removeWrap"
    }, m(Button, {
      className: "Button Button--link VEvt-remove",
      icon: "fas fa-trash",
      onclick: () => this.remove()
    }, "Delete this business")) : null)));
  }
  verification() {
    const v = this.b.verification || {};
    const r = this.reg;
    const ng = r.country === 'NG';
    return m("div", {
      className: "VEvtF-block VBizF-verify"
    }, m("div", {
      className: "VEvtF-blockHead"
    }, m("i", {
      className: "fas fa-shield-halved",
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, "Verification"), m("small", null, v.status === 'verified' ? 'Verified: ' + v.kind + '. Sending new details checks it again.' : v.status === 'pending' ? 'Waiting for a moderator to check the registry.' : v.status === 'rejected' ? 'Not verified: ' + (this.b.verificationNote || 'the details didn’t match the registry.') : 'Get the Verified badge'))), m("p", {
      className: "VEvtF-hint"
    }, ng ? 'A moderator checks the CAC public register for this RC or BN number and name. Only your business’s owners and moderators see the number. We never ask for NIN.' : 'A moderator checks the official business register for this number and name. Only your business’s owners and moderators see it.'), m("div", {
      className: "VBizF-row"
    }, m("select", {
      className: "VBiz-select",
      value: r.country,
      onchange: e => r.country = e.target.value,
      "aria-label": "Registered in"
    }, bizCountries(r.country).map(([code, name]) => m("option", {
      value: code
    }, name))), m("input", {
      maxlength: "40",
      value: r.number,
      oninput: e => r.number = e.target.value,
      placeholder: ng ? 'RC or BN number' : 'Registration number',
      "aria-label": "Registration number"
    })), m("input", {
      maxlength: "160",
      value: r.name,
      oninput: e => r.name = e.target.value,
      placeholder: "Registered name",
      "aria-label": "Registered name"
    }), this.vmsg ? m("p", {
      className: "VEvtF-error"
    }, this.vmsg) : null, m(Button, {
      className: "Button",
      loading: this.verifying,
      onclick: () => this.requestVerification()
    }, v.status === 'none' || !v.status ? 'Ask for verification' : 'Send again'));
  }
}
flarum.reg.add('voucha-community', 'forum/businesses/BusinessForm', BusinessForm);